'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import { setLogLevel, LogLevel } from 'livekit-client';
import styles from '../styles/Home.module.css';

// Suppress LiveKit SDK logs
setLogLevel(LogLevel.silent);

const ROLE_OPTIONS = [
  { value: 'ADMIN',     label: 'Registrar / Admin', type: 'Official',   emoji: '👤', color: '#19747C' },
  { value: 'PURCHASER', label: 'Purchaser',          type: 'Buyer',      emoji: '🛒', color: '#12656D' },
  { value: 'OWNER',     label: 'Owner / Seller',     type: 'Seller',     emoji: '🏠', color: '#7c3aed' },
  { value: 'WITNESS',   label: 'Witness',             type: 'Witness',    emoji: '👁️', color: '#d97706' },
  { value: 'RECIPIENT', label: 'Recipient',           type: 'Party',      emoji: '📋', color: '#19747C' },
];

interface AddedParticipant {
  userId: string;
  partyId: string;
  name: string;
  mobileNumber: string;
  partyRole: string;
  partyType: string;
  source: 'search' | 'manual';
}

function formatMaskedMobile(mobile: string) {
  if (!mobile) return '';
  const digits = mobile.replace(/\D/g, '');
  if (digits.length >= 10) {
    const firstDigit = digits[0];
    return `${firstDigit}•••• •••••`;
  }
  return mobile;
}

function Tabs(props: React.PropsWithChildren<{}>) {
  const searchParams = useSearchParams();
  const isExplicitLogin = searchParams?.get('login') === '1' || searchParams?.get('tab') === 'create';
  const hasMeetingParam = Boolean(
    searchParams?.get('room') ||
    searchParams?.get('access_token') ||
    searchParams?.get('token') ||
    searchParams?.get('t')
  );
  const tabIndex = !isExplicitLogin && (searchParams?.get('tab') === 'join' || hasMeetingParam) ? 1 : 0;

  return (
    <div className="w-full max-w-[1000px] bg-white rounded-2xl md:rounded-[24px] border border-slate-200/90 shadow-sm p-4 sm:p-6 md:p-8">
      {/* Top Bar with Camera Icon + Meeting Setup */}
      <div className="flex items-center justify-between gap-4 pb-5 md:pb-6 border-b border-slate-200/80">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#EAF4F4] border border-[#9DC7CA] flex items-center justify-center text-[#19747C] flex-shrink-0 shadow-xs">
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M4 6h9a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2z" />
            </svg>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Meeting setup</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-6">
        {/* @ts-ignore */}
        {props.children[tabIndex]}
      </div>
    </div>
  );
}

function JoinMeetingTab(props: { label: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const roomParam = searchParams?.get('room');
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  
  const [userType, setUserType] = useState('participant'); // 'participant', 'admin'
  const [adminMode, setAdminMode] = useState('real'); // 'real', 'ai'

  const [role, setRole] = useState('owner'); // 'owner', 'owner_partner', 'purchaser', 'purchaser_partner', 'witness'
  const [witnessNumber, setWitnessNumber] = useState('1'); // '1', '2'
  const [name, setName] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [tokenParam, setTokenParam] = useState('');
  const [verificationStatus, setVerificationStatus] = useState<{
    isChecking: boolean;
    isValid?: boolean;
    error?: string;
    user?: any;
  }>({ isChecking: false });

  const [joinCode, setJoinCode] = useState(roomParam || '');

  // Duplicate-session dialog state
  const [duplicateSessionData, setDuplicateSessionData] = useState<{
    existingJoinUrl: string | null;
    pendingPayload: Record<string, any>;
    pendingNav: { roomName: string; queryParams: string };
  } | null>(null);
  const [forceRejoinLoading, setForceRejoinLoading] = useState(false);

  useEffect(() => {
    const rawToken = searchParams?.get('access_token') || searchParams?.get('token');
    const tParam = searchParams?.get('t');

    const handleVerifyToken = (tokenToVerify: string) => {
      const cleanToken = decodeURIComponent(tokenToVerify).replace(/^"|"$/g, '').trim();
      setTokenParam(cleanToken);
      setVerificationStatus({ isChecking: true });

      const meetingParam = roomParam || '';

      // Immediately sanitize the address bar so no access token or encrypted token is visible
      if (typeof window !== 'undefined') {
        const cleanUrl = meetingParam ? `/?room=${encodeURIComponent(meetingParam)}` : '/';
        window.history.replaceState({}, '', cleanUrl);
      }

      const detailsUrl = `/api/user/details?access_token=${encodeURIComponent(cleanToken)}${
        meetingParam ? `&meetingId=${encodeURIComponent(meetingParam)}` : ''
      }`;

      fetch(detailsUrl)
        .then((res) => res.json())
        .then(async (data) => {
          if (data && data.isValid !== false && (data.name || data.displayName || data.userId)) {
            let userName = data.name || data.displayName || '';
            const partyRole = String(data.partyRole || '').toUpperCase();

            // If name is just a role-word (e.g. "ADMIN", "OWNER"), try to get real name
            // from gov API using the access_token as a session token
            const roleWords = ['ADMIN', 'OWNER', 'PURCHASER', 'WITNESS', 'REGISTRAR', 'HOST', 'PARTICIPANT', 'RECIPIENT'];
            const isJustRoleWord = roleWords.includes(userName.toUpperCase().trim());

            if (isJustRoleWord && cleanToken) {
              try {
                const govRes = await fetch(`/api/user/details?access_token=${encodeURIComponent(cleanToken)}&tryGov=true`);
                const govData = await govRes.json();
                if (govData.isValid && govData.name && !roleWords.includes(govData.name.toUpperCase().trim())) {
                  userName = govData.name;
                }
              } catch {}
            }

            // Final fallback: use mobile number if still just a role word
            if (roleWords.includes(userName.toUpperCase().trim()) && data.mobileNumber) {
              const roleLabel = partyRole === 'ADMIN' ? 'Registrar' :
                partyRole === 'PURCHASER' ? 'Purchaser' :
                partyRole === 'OWNER' ? 'Owner' :
                partyRole === 'WITNESS' ? 'Witness' :
                partyRole === 'RECIPIENT' ? 'Recipient' : partyRole;
              userName = `${data.mobileNumber} (${roleLabel})`;
            }

            setName(userName);
            setVerificationStatus({ isChecking: false, isValid: true, user: { ...data, name: userName } });

            // Detect role from partyRole in the API response first, then fallback to partyType / gov roles
            const pType = String(data.partyType || '').toLowerCase();

            let detectedRole = 'purchaser';
            if (partyRole === 'ADMIN' || partyRole === 'REGISTRAR' || pType === 'official' || data.type === 'EMPLOYEE') {
              detectedRole = 'registrar';
            } else if (partyRole === 'PURCHASER' || pType === 'buyer' || pType === 'purchaser') {
              detectedRole = 'purchaser';
            } else if (partyRole === 'OWNER' || pType === 'seller' || pType === 'owner') {
              detectedRole = 'owner';
            } else if (partyRole === 'WITNESS' || pType === 'witness') {
              detectedRole = 'witness';
            } else if (partyRole === 'RECIPIENT') {
              if (pType === 'buyer' || pType === 'purchaser') {
                detectedRole = 'purchaser';
              } else if (pType === 'seller' || pType === 'owner') {
                detectedRole = 'owner';
              } else {
                detectedRole = 'recipient';
              }
            } else if (data.roles && Array.isArray(data.roles)) {
              const hasAdmin = data.roles.some((r: any) =>
                ['ADMIN', 'REGISTRAR', 'SR', 'EMPLOYEE'].includes(String(r.code || r.name).toUpperCase())
              );
              if (hasAdmin) detectedRole = 'registrar';
            }

            setRole(detectedRole);
            if (detectedRole === 'registrar') {
              setUserType('admin');
            } else {
              setUserType('participant');
            }
          } else {
            const failureUrl = data?.navUrlFailure || (typeof data?.navUrl === 'object' ? data?.navUrl?.failure : null);
            if (failureUrl && typeof failureUrl === 'string' && failureUrl.trim()) {
              window.location.href = failureUrl.trim();
              return;
            }
            if (meetingParam) {
              fetch(`/api/meetings?joinCode=${encodeURIComponent(meetingParam)}`)
                .then((r) => r.json())
                .then((m) => {
                  const fUrl = m?.navUrlFailure || (typeof m?.navUrl === 'object' ? m?.navUrl?.failure : null);
                  if (fUrl && typeof fUrl === 'string' && fUrl.trim()) {
                    window.location.href = fUrl.trim();
                  }
                })
                .catch(() => {});
            }
            setVerificationStatus({
              isChecking: false,
              isValid: false,
              error: data.error || 'User does not exist in user collection. Access token is invalid.',
            });
          }
        })
        .catch(() => {
          if (meetingParam) {
            fetch(`/api/meetings?joinCode=${encodeURIComponent(meetingParam)}`)
              .then((r) => r.json())
              .then((m) => {
                const fUrl = m?.navUrlFailure || (typeof m?.navUrl === 'object' ? m?.navUrl?.failure : null);
                if (fUrl && typeof fUrl === 'string' && fUrl.trim()) {
                  window.location.href = fUrl.trim();
                }
              })
              .catch(() => {});
          }
          setVerificationStatus({
            isChecking: false,
            isValid: false,
            error: 'Failed to verify user in database.',
          });
        });
    };

    if (tParam) {
      fetch(`/api/decrypt-join-params?t=${encodeURIComponent(tParam)}`)
        .then((r) => r.json())
        .then((dec) => {
          const extractedToken = (dec.access_token || dec.token || '') as string;
          if (extractedToken) {
            handleVerifyToken(extractedToken);
          } else if (roomParam) {
            fetch(`/api/meetings?joinCode=${encodeURIComponent(roomParam)}`)
              .then((r) => r.json())
              .then((m) => {
                const fUrl = m?.navUrlFailure || (typeof m?.navUrl === 'object' ? m?.navUrl?.failure : null);
                if (fUrl && typeof fUrl === 'string' && fUrl.trim()) {
                  window.location.href = fUrl.trim();
                }
              })
              .catch(() => {});
          }
        })
        .catch(() => {
          if (roomParam) {
            fetch(`/api/meetings?joinCode=${encodeURIComponent(roomParam)}`)
              .then((r) => r.json())
              .then((m) => {
                const fUrl = m?.navUrlFailure || (typeof m?.navUrl === 'object' ? m?.navUrl?.failure : null);
                if (fUrl && typeof fUrl === 'string' && fUrl.trim()) {
                  window.location.href = fUrl.trim();
                }
              })
              .catch(() => {});
          }
        });
    } else if (rawToken) {
      handleVerifyToken(rawToken);
    }
  }, [searchParams, roomParam]);

  // Navigate to the room after a successful join — params are encrypted
  const navigateToRoom = async (roomName: string, participantId: string, pRole: string, pType: string, identity: string, tabIdParam?: string) => {
    const params: Record<string, string> = {
      name: identity,
      participantId,
      role: pRole,
      type: pType,
    };
    if (tabIdParam) params.tabId = tabIdParam;
    if (tokenParam) params.access_token = tokenParam;
    if (verificationStatus.user?.docRegId) params.docRegId = verificationStatus.user.docRegId;

    try {
      // Encrypt all params server-side so the URL is opaque
      const encRes = await fetch('/api/encrypt-join-params', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const { token } = await encRes.json();
      const dest = `/rooms/${roomName}?t=${encodeURIComponent(token)}`;
      if (e2ee && sharedPassphrase) {
        // Use replace so the join form is not in browser Back history
        router.replace(`${dest}#${encodePassphrase(sharedPassphrase)}`);
      } else {
        router.replace(dest);
      }
    } catch {
      // Fallback: navigate with plain params if encryption fails
      let queryParams = `name=${encodeURIComponent(identity)}&participantId=${encodeURIComponent(participantId)}&role=${encodeURIComponent(pRole)}&type=${encodeURIComponent(pType)}`;
      if (tabIdParam) queryParams += `&tabId=${encodeURIComponent(tabIdParam)}`;
      if (tokenParam) queryParams += `&access_token=${encodeURIComponent(tokenParam)}`;
      if (verificationStatus.user?.docRegId) queryParams += `&docRegId=${encodeURIComponent(verificationStatus.user.docRegId)}`;
      router.replace(`/rooms/${roomName}?${queryParams}`);
    }
  };


  const startMeeting = async (opts?: { forceRejoin?: boolean }) => {
    const isRegistrar = role === 'registrar' || userType === 'admin';
    let finalName = name.trim();
    if (isRegistrar) {
      finalName = finalName || (verificationStatus.user?.displayName || 'Registrar');
    } else if (!finalName) {
      toast.warning('Please enter your name');
      return;
    }

    if (!joinCode.trim()) {
      toast.warning('Please enter a meeting join code');
      return;
    }

    // Build clean identity: "Real Name - Role"
    const roleLabel = role === 'registrar' ? 'Registrar' :
      role === 'owner' ? 'Owner' :
      role === 'purchaser' ? 'Purchaser' :
      role === 'witness' ? 'Witness' :
      role === 'recipient' ? 'Recipient' : 'Participant';

    let identity = '';
    if (isRegistrar) {
      identity = finalName.toLowerCase().includes('registrar') ? finalName : `${finalName} - Registrar`;
    } else {
      let roleString = roleLabel;
      if (role === 'witness') roleString = `Witness ${witnessNumber}`;
      identity = finalName.toLowerCase().includes(roleString.toLowerCase()) ? finalName : `${finalName} - ${roleString}`;
    }

    setIsJoining(true);

    try {
      const sendRole = isRegistrar ? 'HOST' : (role === 'recipient' ? 'RECIPIENT' : (role === 'owner' ? 'OWNER' : role === 'purchaser' ? 'PURCHASER' : role.toUpperCase()));
      const sendType = isRegistrar ? 'ADMIN' : (role === 'owner' ? 'Seller' : role === 'purchaser' ? 'Buyer' : role === 'recipient' ? 'Recipient' : 'Citizen');

      const currentTabId = typeof window !== 'undefined'
        ? (window.sessionStorage.getItem('lk_tab_id') || `tab_${Math.random().toString(36).slice(2, 11)}_${Date.now()}`)
        : '';
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('lk_tab_id', currentTabId);
      }

      const payload: Record<string, any> = {
        joinCode: joinCode.trim(),
        participantName: identity,
        role: sendRole,
        type: sendType,
        accessToken: tokenParam || undefined,
        tabId: currentTabId,
      };
      if (opts?.forceRejoin) payload.forceRejoin = true;

      const response = await fetch('/api/join-meeting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.status === 409 || data.code === 'already_joined') {
        // User is already in the meeting — show choice dialog instead of alert
        setIsJoining(false);
        setDuplicateSessionData({
          existingJoinUrl: data.existingJoinUrl || null,
          pendingPayload: payload,
          pendingNav: { roomName: '', queryParams: '' }, // will be filled on success
        });
        return;
      }

      if (!response.ok) {
        setIsJoining(false);
        toast.error(data.error || 'Failed to join meeting');
        return;
      }

      const roomName = data.roomName;
      const participantId = data.participantId || data.id;
      const pRole = data.role || sendRole;
      const pType = data.type || sendType;

      navigateToRoom(roomName, participantId, pRole, pType, identity, data.tabId || currentTabId);
    } catch (err) {
      setIsJoining(false);
      toast.error('Network error while joining meeting');
    }
  };

  // Force-rejoin: kick the old session and join from this device
  const handleContinueHere = async () => {
    if (!duplicateSessionData) return;
    setForceRejoinLoading(true);
    try {
      const response = await fetch('/api/join-meeting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...duplicateSessionData.pendingPayload, forceRejoin: true }),
      });
      const data = await response.json();
      if (response.ok) {
        const identity = duplicateSessionData.pendingPayload.participantName;
        const sendRole = duplicateSessionData.pendingPayload.role;
        const sendType = duplicateSessionData.pendingPayload.type;
        setDuplicateSessionData(null);
        navigateToRoom(data.roomName, data.participantId || data.id, data.role || sendRole, data.type || sendType, identity, data.tabId);
      } else {
        setDuplicateSessionData(null);
        toast.error(data.error || 'Failed to rejoin meeting');
      }
    } catch {
      setDuplicateSessionData(null);
      toast.error('Network error while rejoining');
    } finally {
      setForceRejoinLoading(false);
    }
  };


  const handleRoleChange = (newRole: string) => {
    setRole(newRole);
    if (newRole === 'registrar') {
      setUserType('admin');
    } else {
      setUserType('participant');
    }
  };

  return (
    <React.Fragment>
      {/* ── Duplicate-session choice dialog ─────────────────────────────────── */}
      {duplicateSessionData && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-[28px] p-8 max-w-[460px] w-full shadow-[0_24px_60px_rgba(0,0,0,0.14)] border border-slate-100 flex flex-col items-center text-center">

            {/* Icon */}
            <div className="w-20 h-20 bg-amber-50 border-2 border-amber-100 rounded-full flex items-center justify-center mb-5">
              <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h2 className="text-[22px] font-bold text-slate-900 mb-1 tracking-tight">Already in this meeting</h2>
            <p className="text-slate-500 text-sm leading-relaxed mb-6 max-w-sm">
              Exit from the previous window. If you join from here then the previous session expires automatically.
            </p>

            <div className="w-full flex flex-col gap-3">

              {/* Disconnect from old tab and continue from here — kicks old session */}
              <button
                onClick={handleContinueHere}
                disabled={forceRejoinLoading}
                className="w-full py-4 px-5 rounded-2xl bg-[#19747C] hover:bg-[#12656D] text-white font-semibold text-sm shadow-sm transition-all flex items-center justify-center gap-2.5 disabled:opacity-60 cursor-pointer"
              >
                {forceRejoinLoading
                  ? (
                    <React.Fragment>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                      <span>Disconnecting &amp; continuing&hellip;</span>
                    </React.Fragment>
                  )
                  : (
                    <React.Fragment>
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0H3" />
                      </svg>
                      <span>Exit previous window &amp; join here</span>
                    </React.Fragment>
                  )
                }
              </button>

              {/* Cancel */}
              <button
                onClick={() => setDuplicateSessionData(null)}
                disabled={forceRejoinLoading}
                className="w-full py-3 text-slate-400 hover:text-slate-600 text-sm font-medium transition-colors disabled:opacity-40 cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─────────────────────────────────────────────────────────────────────── */}

    <div className="max-w-xl mx-auto space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">Join a meeting</h2>
        <p className="text-sm text-slate-500 mt-1">Enter the meeting join code and details below to connect.</p>
      </div>

      {verificationStatus.isChecking && (
        <div className="p-3 bg-[#EAF4F4] border border-[#9DC7CA] text-[#19747C] rounded-lg text-sm flex items-center gap-2">
          <span>🔍</span>
          <span>Verifying Access Token in Database...</span>
        </div>
      )}



      {verificationStatus.isValid === true && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-sm flex items-center gap-2.5">
          <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1 min-w-0">
            <span className="font-semibold text-emerald-900">Verified Participant: </span>
            <span className="font-medium text-emerald-800">{name || verificationStatus.user?.displayName || 'Verified'}</span>
            <span className="text-emerald-700 text-xs ml-1.5 font-normal">({role === 'owner' ? 'Owner / Seller' : role === 'purchaser' ? 'Purchaser' : role === 'registrar' ? 'Registrar' : role === 'witness' ? 'Witness' : role})</span>
          </div>
        </div>
      )}

      {verificationStatus.isValid === false && (
        <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
          <div className="font-semibold">Direct Participant Mode:</div>
          <div className="text-xs mt-0.5">Joining directly without verification.</div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Meeting Join Code</label>
          <input 
            type="text" 
            placeholder="e.g. 414-341-618" 
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            readOnly={verificationStatus.isValid === true}
            className={`w-full border rounded-lg px-3.5 py-2.5 text-sm text-slate-800 outline-none shadow-xs ${
              verificationStatus.isValid === true
                ? 'border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed'
                : 'border-slate-300 focus:border-[#19747C] focus:ring-1 focus:ring-[#19747C]'
            }`}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Select Your Role</label>
          <div className="relative">
            <select 
              value={role} 
              onChange={(e) => handleRoleChange(e.target.value)}
              disabled={verificationStatus.isValid === true}
              className={`w-full appearance-none border rounded-lg px-3.5 py-2.5 pr-8 text-sm font-medium text-slate-800 outline-none shadow-xs ${
                verificationStatus.isValid === true
                  ? 'border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed'
                  : 'border-slate-300 focus:border-[#19747C] cursor-pointer'
              }`}
            >
              <option value="registrar">Registrar / Admin</option>
              <option value="purchaser">Purchaser Party (Buyer)</option>
              <option value="owner">Owner / Seller Party</option>
              <option value="witness">Witness</option>
              <option value="recipient">Recipient</option>
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
              {verificationStatus.isValid === true ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </div>
          </div>
          {verificationStatus.isValid === true && (
            <p className="mt-1 text-[11px] text-slate-400">Role is pre-filled from your login credentials.</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Your Full Name</label>
          <input 
            type="text" 
            placeholder="Your Full Name" 
            value={name}
            onChange={(e) => setName(e.target.value)}
            readOnly={verificationStatus.isValid === true}
            className={`w-full border rounded-lg px-3.5 py-2.5 text-sm text-slate-800 outline-none shadow-xs ${
              verificationStatus.isValid === true
                ? 'border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed'
                : 'border-slate-300 focus:border-[#19747C] focus:ring-1 focus:ring-[#19747C]'
            }`}
          />
        </div>
        

      </div>

      <div className="pt-2">
        <button
          onClick={() => startMeeting()}
          disabled={isJoining}
          className="w-full bg-[#19747C] hover:bg-[#12656D] text-white font-semibold py-2.5 px-4 rounded-lg text-sm flex items-center justify-center gap-2 transition shadow-xs cursor-pointer"
        >
          {isJoining ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeDasharray="32" strokeLinecap="round" opacity="0.3"></circle>
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"></path>
              </svg>
              <span>Joining...</span>
            </>
          ) : (
            'Start Meeting'
          )}
        </button>
      </div>
      

    </div>
    </React.Fragment>
  );
}

function CreateMeetingTab(props: { label: string }) {
  const searchParams = useSearchParams();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 — Registrar identity
  const [loginMode, setLoginMode] = useState<'credentials' | 'token'>('credentials');
  // credentials sub-flow
  const [credMobile, setCredMobile]     = useState('');
  const [credPassword, setCredPassword] = useState('');
  const [showCredPassword, setShowCredPassword] = useState(false);
  const [credUserType, setCredUserType] = useState('EMPLOYEE');
  const [credOtp, setCredOtp]           = useState('');
  const [credStep, setCredStep]         = useState<'form' | 'otp'>('form');
  const [credLoading, setCredLoading]   = useState(false);
  const [credError, setCredError]       = useState('');
  // token paste sub-flow
  const [regToken, setRegToken]   = useState('');
  const [regUser, setRegUser]     = useState<any>(null);
  const [regVerifying, setRegVerifying] = useState(false);
  const [regError, setRegError]   = useState('');

  // Step 2 — Participants
  const [searchMobile, setSearchMobile] = useState('');
  const [searchRole, setSearchRole] = useState('OWNER');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [participants, setParticipants] = useState<AddedParticipant[]>([]);

  // Step 3 — Meeting details
  const [title, setTitle]               = useState('');
  const [purpose, setPurpose]           = useState('');
  const [docRegId, setDocRegId]         = useState('');
  const [navUrlSuccess, setNavUrlSuccess] = useState('');
  const [navUrlFailure, setNavUrlFailure] = useState('');
  const [isCreating, setIsCreating]     = useState(false);
  const [createdMeeting, setCreatedMeeting] = useState<any>(null);
  const [copiedLink, setCopiedLink]     = useState(false);
  const [copiedCode, setCopiedCode]     = useState(false);
  const [copiedParticipantIndex, setCopiedParticipantIndex] = useState<number | null>(null);

  // ── Step 1 helpers ───────────────────────────────────────────────────────────
  const admitRegistrar = (data: any) => {
    setRegUser(data);
    const regUuid = String(data.uuid || data.userId || '').trim().toLowerCase();
    const regMobile = String(data.mobileNumber || '').trim().replace(/\D/g, '');

    setParticipants(prev => {
      // Check if admin/registrar or matching user is already in participants list
      const exists = prev.some(p => {
        const pId = String(p.userId || p.partyId || '').trim().toLowerCase();
        const pMobile = String(p.mobileNumber || '').trim().replace(/\D/g, '');
        return (
          p.partyRole === 'ADMIN' ||
          (regUuid && pId === regUuid) ||
          (regMobile && pMobile && pMobile.length >= 10 && pMobile.endsWith(regMobile.slice(-10)))
        );
      });

      if (exists) {
        // If already present, update the existing ADMIN participant's details in place instead of adding a duplicate
        return prev.map(p => {
          if (
            p.partyRole === 'ADMIN' ||
            (regUuid && String(p.userId || p.partyId || '').trim().toLowerCase() === regUuid) ||
            (regMobile && String(p.mobileNumber || '').trim().replace(/\D/g, '').endsWith(regMobile.slice(-10)))
          ) {
            return {
              ...p,
              userId: data.uuid || data.userId || p.userId,
              partyId: data.uuid || data.userId || p.partyId,
              name: data.name || data.firstName || p.name,
              mobileNumber: data.mobileNumber || p.mobileNumber,
              partyRole: 'ADMIN',
              partyType: 'Official',
            };
          }
          return p;
        });
      }

      // If not present, add registrar at the top of the list
      return [
        {
          userId: data.uuid || data.userId || '',
          partyId: data.uuid || data.userId || '',
          name: data.name || data.firstName || 'Registrar',
          mobileNumber: data.mobileNumber || '',
          partyRole: 'ADMIN',
          partyType: 'Official',
          source: 'search',
        },
        ...prev,
      ];
    });

    setStep(2);
    if (typeof window !== 'undefined') {
      window.history.pushState({ wizardStep: 2 }, '', '/?tab=create');
    }
  };

  // Verify by pasting an existing token
  const verifyRegistrar = async () => {
    if (!regToken.trim()) { setRegError('Enter your official access token'); return; }
    setRegVerifying(true); setRegError('');
    try {
      const res = await fetch(`/api/user/details?access_token=${encodeURIComponent(regToken.trim())}`);
      const data = await res.json();
      if (!res.ok || !data.isValid) throw new Error(data.error || 'Invalid access token');
      admitRegistrar(data);
    } catch (e: any) {
      setRegError(e.message);
    } finally {
      setRegVerifying(false);
    }
  };

  // Login with mobile + password → OTP → get token automatically
  const handleCredPreLogin = async () => {
    const cleanedMobile = credMobile.replace(/\D/g, '').slice(0, 10);
    if (!cleanedMobile || !credPassword.trim()) { setCredError('Mobile and password are required'); return; }
    if (cleanedMobile.length !== 10) { setCredError('Please enter a valid 10-digit mobile number'); return; }
    setCredLoading(true); setCredError('');
    try {
      const res = await fetch('/api/gov/auth', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step: 'prelogin', userName: cleanedMobile, password: credPassword.trim(), userType: credUserType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Pre-login failed');
      setCredStep('otp');
    } catch (e: any) { setCredError(e.message); }
    finally { setCredLoading(false); }
  };

  const handleCredLogin = async () => {
    if (!credOtp.trim()) { setCredError('Enter the OTP'); return; }
    setCredLoading(true); setCredError('');
    try {
      const res = await fetch('/api/gov/auth', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step: 'login', userName: credMobile.replace(/\D/g, '').slice(0, 10), password: credPassword.trim(), otp: credOtp.trim(), userType: credUserType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      // Store access token for later participant search
      setRegToken(data.accessToken);
      // Map login user to our format
      const u = data.user;
      admitRegistrar({
        isValid: true, uuid: u.uuid, userId: u.uuid,
        name: u.firstName + (u.lastName ? ' ' + u.lastName : ''),
        firstName: u.firstName, lastName: u.lastName,
        mobileNumber: u.mobileNumber, emailId: u.emailId,
        type: u.type, tenantId: u.tenantId, roles: u.roles,
      });
    } catch (e: any) { setCredError(e.message); }
    finally { setCredLoading(false); }
  };

  // ── Step 2: Search participant by mobile ─────────────────────────────────────
  const searchParticipant = async () => {
    const cleanedSearchMobile = searchMobile.replace(/\D/g, '').slice(0, 10);
    if (!cleanedSearchMobile) { setSearchError('Enter a mobile number'); return; }
    if (cleanedSearchMobile.length !== 10) { setSearchError('Please enter a valid 10-digit mobile number'); return; }
    setSearchLoading(true); setSearchError('');
    try {
      const res = await fetch('/api/gov/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken: regToken.trim(),
          users: [{ mobileNumber: cleanedSearchMobile, partyRole: searchRole, partyType: ROLE_OPTIONS.find(r => r.value === searchRole)?.type || 'CITIZEN' }],
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Search failed');
      if (data.notFound?.length > 0 || !data.userDetails?.length) {
        throw new Error(`No user record found for mobile ${searchMobile}`);
      }
      const found = data.userDetails[0];
      const foundMobile = String(found.mobileNumber || searchMobile).trim().replace(/\D/g, '');
      const foundUuid = String(found.userId || found.partyId || '').trim().toLowerCase();

      const alreadyAdded = participants.some(p => {
        const pId = String(p.userId || p.partyId || '').trim().toLowerCase();
        const pMobile = String(p.mobileNumber || '').trim().replace(/\D/g, '');
        return (
          (foundUuid && pId === foundUuid) ||
          (foundMobile && pMobile && pMobile.length >= 10 && pMobile.endsWith(foundMobile.slice(-10)))
        );
      });

      if (alreadyAdded) {
        setSearchError('This participant is already added'); return;
      }
      setParticipants(prev => [...prev, {
        userId: found.userId, 
        partyId: found.partyId || found.userId,
        name: found.name || '', 
        mobileNumber: found.mobileNumber || searchMobile,
        partyRole: found.partyRole, 
        partyType: found.partyType, 
        source: 'search',
      }]);
      setSearchMobile('');
    } catch (e: any) {
      setSearchError(e.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const removeParticipant = (userId: string) =>
    setParticipants(prev => prev.filter(p => p.userId !== userId));

  const updateParticipantRole = (userId: string, role: string) =>
    setParticipants(prev => prev.map(p => {
      if (p.userId !== userId) return p;
      const opt = ROLE_OPTIONS.find(r => r.value === role);
      return { ...p, partyRole: role, partyType: opt?.type || p.partyType };
    }));

  // ── Step 3: Create meeting ───────────────────────────────────────────────────
  const createMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!participants.length) { toast.warning('Add at least one participant'); return; }
    setIsCreating(true);
    try {
      const res = await fetch('/api/meetings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title, purpose,
          docRegId: docRegId || undefined,
          scheduledStart: new Date().toISOString(),
          scheduledEnd: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
          timezone: 'Asia/Kolkata',
          createdByUserId: regUser?.uuid,
          createdByRole: 'registrar',
          accessToken: regToken || undefined,
          govAccessToken: regToken || undefined,
          navUrlSuccess: navUrlSuccess || undefined,
          navUrlFailure: navUrlFailure || undefined,
          navUrl: (navUrlSuccess || navUrlFailure) ? { success: navUrlSuccess || '', failure: navUrlFailure || '' } : undefined,
          userDetails: participants.map(p => ({
            userId: p.userId,
            partyId: p.partyId,
            name: p.name,
            mobileNumber: p.mobileNumber,
            partyType: p.partyType,
            partyRole: p.partyRole,
          })),
          settings: {
            waitingRoomEnabled: false, lobbyOpensBeforeMin: 15,
            verificationRequired: false, verificationMode: 'NONE',
            captureIntervalMin: 10, maxParticipants: 50,
            allowGuestJoin: true, muteOnEntry: false,
          },
          compliance: { consentTextVersion: 'v2.1', dataRetentionDays: 90 },
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setCreatedMeeting(data);
        if (typeof window !== 'undefined') {
          window.history.pushState({ wizardStep: 'created' }, '', '/?tab=create');
        }
      }
      else toast.error('Error: ' + (data.error || 'Unknown error'));
    } catch { toast.error('Network error'); }
    finally { setIsCreating(false); }
  };

  const resetAll = () => {
    setLoginMode('credentials'); setCredStep('form');
    setCredMobile(''); setCredPassword(''); setShowCredPassword(false); setCredOtp(''); setCredError('');
    setStep(1); setRegToken(''); setRegUser(null); setRegError('');
    setParticipants([]); setSearchMobile(''); setSearchError('');
    setTitle(''); setPurpose(''); setDocRegId('');
    setNavUrlSuccess(''); setNavUrlFailure(''); setCreatedMeeting(null);
    setCopiedLink(false); setCopiedCode(false); setCopiedParticipantIndex(null);
  };

  // Reset wizard to Step 1 (Official Login / Verify identity) whenever login redirect or reset is triggered
  useEffect(() => {
    const isLoginRedirect = searchParams?.get('login') === '1' || searchParams?.get('reset') === '1';
    if (isLoginRedirect) {
      resetAll();
      if (typeof window !== 'undefined') {
        window.history.replaceState({}, '', '/?tab=create');
      }
    }
  }, [searchParams]);

  // Safety guard: If user somehow is on step 2 (Add participants) or step 3 without verified identity/token,
  // immediately force back to step 1 (Official Login / Verify identity)
  const goToStep3 = () => {
    setStep(3);
    if (typeof window !== 'undefined') {
      window.history.pushState({ wizardStep: 3 }, '', '/?tab=create');
    }
  };

  const handleBackToStep1 = () => {
    setCredStep('form');
    setCredOtp('');
    setCredError('');
    setRegUser(null);
    setRegToken('');
    setStep(1);
    if (typeof window !== 'undefined') {
      window.history.replaceState({ wizardStep: 1 }, '', '/?tab=create');
    }
  };

  useEffect(() => {
    if (step > 1 && !regUser && !regToken) {
      setCredStep('form');
      setCredOtp('');
      setStep(1);
    }
  }, [step, regUser, regToken]);

  useEffect(() => {
    if (typeof window !== 'undefined' && searchParams?.get('tab') !== 'create' && !searchParams?.get('room')) {
      window.history.replaceState({ wizardStep: step }, '', '/?tab=create');
    }
  }, []);

  useEffect(() => {
    const handlePopState = (_e: PopStateEvent) => {
      if (typeof window !== 'undefined' && !window.location.search.includes('tab=create')) {
        window.history.replaceState({}, '', '/?tab=create');
      }

      if (createdMeeting) {
        setCreatedMeeting(null);
        setStep(3);
        return;
      }

      if (step === 3) {
        setStep(2);
        return;
      }

      if (step === 2) {
        handleBackToStep1();
        return;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [createdMeeting, step]);

  // ── Success State ───────────────────────────────────────────────────────────
  if (createdMeeting) {
    // Build the join URL — no access_token. Participants authenticate themselves and join.
    const joinUrl = createdMeeting.joinUrl || createdMeeting.baseJoinUrl ||
      `${typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8080'}/join_meeting/${createdMeeting.publicJoinCode}`;

    const copyToClipboard = async (text: string) => {
      let succeeded = false;
      if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          succeeded = true;
        } catch (_) {}
      }
      if (!succeeded && typeof document !== 'undefined') {
        try {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-9999px';
          textArea.style.top = '-9999px';
          textArea.setAttribute('readonly', '');
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          succeeded = document.execCommand('copy');
          textArea.remove();
        } catch (_) {}
      }
      return succeeded;
    };

    const copyUrl = async () => {
      await copyToClipboard(joinUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    };

    const copyCode = async () => {
      await copyToClipboard(createdMeeting.publicJoinCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    };

    const list: any[] = createdMeeting.userDetails || [];

    return (
      <div className="max-w-xl mx-auto space-y-6 py-4">
        {/* Success Icon + Title */}
        <div className="text-center space-y-2">
          <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-slate-900">Meeting Created Successfully!</h3>
          <p className="text-sm text-slate-500">Share the meeting link below with all participants.</p>
        </div>



        {/* Single Shareable Link */}
        <div className="bg-[#EAF4F4] border border-[#9DC7CA] rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#E5F0F1] rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-[#19747C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-bold text-[#19747C]">Meeting Base Link</p>
              <p className="text-[11px] text-[#607477]">Share this link with all participants to join</p>
            </div>
          </div>

          {/* URL Display */}
          <div className="bg-white border border-[#9DC7CA] rounded-xl px-3.5 py-3 flex items-center gap-3">
            <p className="flex-1 text-sm font-mono text-slate-700 break-all select-all">
              {joinUrl}
            </p>
            <button
              type="button"
              onClick={copyUrl}
              className={`flex-shrink-0 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-200 cursor-pointer flex items-center gap-1.5 shadow-sm ${
                copiedLink
                  ? 'bg-emerald-600 hover:bg-emerald-700 scale-105'
                  : 'bg-[#19747C] hover:bg-[#12656D]'
              }`}
            >
              {copiedLink ? (
                <>
                  <svg className="w-3.5 h-3.5 text-white animate-in zoom-in duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>

          {/* Info note */}
          <div className="flex items-start gap-2 text-[11px] text-[#19747C]">
            <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              Participants can open this link directly. If authenticated with their access token, their role and details are automatically verified.
            </span>
          </div>
        </div>

        {/* Participants Summary */}
        {list.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Participants ({list.length})
            </p>
            <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
              {list.map((u: any, i: number) => {
                const ro = ROLE_OPTIONS.find(r => r.value === u.partyRole) || ROLE_OPTIONS[0];
                const p = participants.find(pt => pt.userId === u.userId);
                const isRegistrar = u.partyRole === 'ADMIN';
                const directUrl = u.joinUrl || joinUrl;
                return (
                  <div key={i} className="flex items-center gap-3.5 px-4 py-3 bg-white hover:bg-slate-50/70 transition">
                    {/* Avatar */}
                    {isRegistrar ? (
                      <div className="w-9 h-9 rounded-full bg-[#EAF4F4] text-[#19747C] font-bold text-sm flex items-center justify-center flex-shrink-0">
                        {(p?.name || u.name || 'R')[0].toUpperCase()}
                      </div>
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                    {/* Name & mobile */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-bold text-slate-800">
                          {p?.name || u.name || ro.label}
                        </span>
                        {isRegistrar && <span className="text-xs text-slate-400">(You)</span>}
                      </div>
                      {(p?.mobileNumber || u.mobileNumber) && (
                        <p className="text-xs text-slate-400 mt-0.5 font-mono">
                          {isRegistrar ? (p?.mobileNumber || u.mobileNumber) : formatMaskedMobile(p?.mobileNumber || u.mobileNumber)}
                        </p>
                      )}
                    </div>
                    {/* Role badge */}
                    <span
                      className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: ro.color + '15', color: ro.color }}
                    >
                      {isRegistrar ? 'Admin' : ro.label}
                    </span>

                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button
          onClick={resetAll}
          className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold py-2.5 px-4 rounded-lg text-sm transition cursor-pointer"
        >
          Create Another Meeting
        </button>
      </div>
    );
  }

  // ── Normal Wizard Workflow ───────────────────────────────────────────────────
  return (
    <div>
      {/* ── Mobile Horizontal Stepper (Image 2) ── */}
      <div className="block md:hidden pb-5 mb-5 border-b border-slate-100">
        <div className="flex items-center justify-between">
          {/* Step 1 */}
          <div
            onClick={() => { if (step > 1) handleBackToStep1(); }}
            className={`flex flex-col items-center flex-1 ${step > 1 ? 'cursor-pointer' : ''}`}
          >
            {step > 1 ? (
              <div className="w-7 h-7 rounded-full bg-[#30A250] flex items-center justify-center text-white text-xs font-bold">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                step === 1 ? 'bg-[#19747C] text-white' : 'border border-slate-300 text-slate-400 bg-white'
              }`}>
                1
              </div>
            )}
            <span className={`text-[11px] mt-1 text-center font-medium ${step === 1 ? 'text-[#19747C] font-bold' : 'text-[#607477]'}`}>
              Verify identity
            </span>
          </div>

          {/* Connector 1 */}
          <div className={`h-[1.5px] flex-1 mx-2 -mt-4 ${step > 1 ? 'bg-slate-300' : 'bg-slate-200'}`} />

          {/* Step 2 */}
          <div className="flex flex-col items-center flex-1">
            {step > 2 ? (
              <div className="w-7 h-7 rounded-full bg-[#30A250] flex items-center justify-center text-white text-xs font-bold">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                step === 2 ? 'bg-[#19747C] text-white' : 'border border-slate-300 text-slate-400 bg-white'
              }`}>
                2
              </div>
            )}
            <span className={`text-[11px] mt-1 text-center font-medium ${step === 2 ? 'text-[#19747C] font-bold' : 'text-[#607477]'}`}>
              Participants
            </span>
          </div>

          {/* Connector 2 */}
          <div className={`h-[1.5px] flex-1 mx-2 -mt-4 ${step > 2 ? 'bg-slate-300' : 'bg-slate-200'}`} />

          {/* Step 3 */}
          <div className="flex flex-col items-center flex-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              step === 3 ? 'bg-[#19747C] text-white' : 'border border-slate-300 text-slate-400 bg-white'
            }`}>
              3
            </div>
            <span className={`text-[11px] mt-1 text-center font-medium ${step === 3 ? 'text-[#19747C] font-bold' : 'text-[#607477]'}`}>
              Meeting details
            </span>
          </div>
        </div>
      </div>

      {/* ── 2-Column Grid (Image 1 Desktop Layout) ── */}
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] lg:grid-cols-[240px_1fr] md:gap-8 lg:gap-10">
        
        {/* Left Sidebar (Desktop Wizard Stepper) */}
        <div className="hidden md:block pr-6 border-r border-slate-200/80">
          <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase block mb-6">
            Meeting setup
          </span>
          <div className="space-y-1">
            
            {/* Step 1 */}
            <div
              onClick={() => { if (step > 1) handleBackToStep1(); }}
              className={`flex items-start gap-3 p-2 rounded-xl transition ${step === 1 ? 'bg-[#EAF4F4]' : ''} ${step > 1 ? 'cursor-pointer hover:bg-slate-50' : ''}`}
            >
              {step > 1 ? (
                <div className="w-7 h-7 rounded-full bg-[#30A250] flex items-center justify-center text-white flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  step === 1 ? 'bg-[#19747C] text-white' : 'border border-slate-300 text-slate-400 bg-white'
                }`}>
                  1
                </div>
              )}
              <div>
                <div className={`text-sm font-semibold ${step === 1 ? 'text-[#19747C] font-bold' : 'text-[#1A1B1C]'}`}>
                  Verify identity
                </div>
                <div className={`text-xs ${step === 1 ? 'text-[#19747C]/80 font-medium' : 'text-[#607477]'}`}>
                  {step > 1 ? 'Completed' : 'Current step'}
                </div>
              </div>
            </div>

            {/* Vertical Connector */}
            <div className="w-[1.5px] bg-slate-200 h-7 ml-5.5" />

            {/* Step 2 */}
            <div className={`flex items-start gap-3 p-2 rounded-xl transition ${step === 2 ? 'bg-[#EAF4F4]' : ''}`}>
              {step > 2 ? (
                <div className="w-7 h-7 rounded-full bg-[#30A250] flex items-center justify-center text-white flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  step === 2 ? 'bg-[#19747C] text-white' : 'border border-slate-300 text-slate-400 bg-white'
                }`}>
                  2
                </div>
              )}
              <div>
                <div className={`text-sm font-semibold ${step === 2 ? 'text-[#19747C] font-bold' : 'text-[#1A1B1C]'}`}>
                  Add participants
                </div>
                <div className={`text-xs ${step === 2 ? 'text-[#19747C]/80 font-medium' : 'text-[#607477]'}`}>
                  {step > 2 ? 'Completed' : step === 2 ? 'Current step' : 'Up next'}
                </div>
              </div>
            </div>

            {/* Vertical Connector */}
            <div className="w-[1.5px] bg-slate-200 h-7 ml-5.5" />

            {/* Step 3 */}
            <div className={`flex items-start gap-3 p-2 rounded-xl transition ${step === 3 ? 'bg-[#EAF4F4]' : ''}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                step === 3 ? 'bg-[#19747C] text-white' : 'border border-slate-300 text-slate-400 bg-white'
              }`}>
                3
              </div>
              <div>
                <div className={`text-sm font-semibold ${step === 3 ? 'text-[#19747C] font-bold' : 'text-[#1A1B1C]'}`}>
                  Meeting details
                </div>
                <div className={`text-xs ${step === 3 ? 'text-[#19747C]/80 font-medium' : 'text-[#607477]'}`}>
                  {step === 3 ? 'Current step' : 'Up next'}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Right Content Area */}
        <div className="min-w-0">

          {/* ────────────────── STEP 1: VERIFY REGISTRAR IDENTITY ────────────────── */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <div className="text-[11px] font-bold tracking-wider text-[#19747C] uppercase mb-1">
                  STEP 1 OF 3
                </div>
                <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">Verify identity</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Authenticate admin account to initialize the secure video conference.
                </p>
              </div>

              <div className="border border-slate-200 rounded-xl p-5 bg-white shadow-xs max-w-lg space-y-4">
                {credStep === 'form' ? (
                  <>
                    <div className="font-bold text-sm text-slate-800">🔐 Official Login</div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">Mobile Number</label>
                      <div className="flex items-center border border-slate-300 rounded-lg bg-white px-3 py-2 focus-within:border-[#19747C] focus-within:ring-1 focus-within:ring-[#19747C] shadow-xs">
                        <span className="text-sm font-semibold text-slate-700 flex items-center gap-1 select-none">
                          +91
                          <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                        <div className="w-[1px] h-5 bg-slate-200 mx-2.5" />
                        <input
                          type="tel"
                          placeholder="10-digit mobile number"
                          maxLength={10}
                          value={credMobile}
                          onChange={e => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setCredMobile(val);
                            setCredError('');
                          }}
                          className="w-full text-sm text-slate-800 placeholder:text-slate-400 bg-transparent border-none outline-none p-0"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-semibold text-slate-700">Password</label>
                        <span className="text-[11px] text-slate-400">Normal or encoded password</span>
                      </div>
                      <div className="relative flex items-center">
                        <input
                          type={showCredPassword ? 'text' : 'password'}
                          placeholder="enter your password"
                          value={credPassword}
                          onChange={e => { setCredPassword(e.target.value); setCredError(''); }}
                          className="w-full border border-slate-300 rounded-lg pl-3.5 pr-10 py-2 text-sm text-slate-800 outline-none focus:border-[#19747C] focus:ring-1 focus:ring-[#19747C] shadow-xs"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCredPassword(!showCredPassword)}
                          className="absolute right-3 text-slate-400 hover:text-slate-600 focus:outline-none cursor-pointer"
                          aria-label={showCredPassword ? 'Hide password' : 'Show password'}
                        >
                          {showCredPassword ? (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>


                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">User Type</label>
                      <select
                        value={credUserType}
                        onChange={e => setCredUserType(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm font-medium text-slate-800 outline-none focus:border-[#19747C] shadow-xs"
                      >
                        <option value="EMPLOYEE">Admin / Registrar</option>
                        <option value="CITIZEN">CITIZEN</option>
                      </select>
                    </div>

                    {credError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-600 font-medium">
                        ⛔ {credError}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleCredPreLogin}
                      disabled={credLoading}
                      className="w-full bg-[#19747C] hover:bg-[#12656D] text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition shadow-xs cursor-pointer"
                    >
                      {credLoading ? '⏳ Sending OTP...' : 'Send OTP →'}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="space-y-1">
                      <p className="font-bold text-sm text-[#30A250]">📱 OTP sent to {credMobile}</p>
                      <p className="text-xs text-slate-500">Enter the OTP received on your registered mobile number.</p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">One-Time Password (OTP)</label>
                      <input
                        type="text"
                        placeholder="• • • • • •"
                        maxLength={6}
                        value={credOtp}
                        onChange={e => { setCredOtp(e.target.value); setCredError(''); }}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleCredLogin())}
                        className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-base text-center tracking-[0.25em] font-mono text-slate-900 outline-none focus:border-[#19747C] focus:ring-1 focus:ring-[#19747C] shadow-xs"
                      />
                    </div>

                    {credError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-600 font-medium">
                        ⛔ {credError}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleCredLogin}
                      disabled={credLoading}
                      className="w-full bg-[#19747C] hover:bg-[#12656D] text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition shadow-xs cursor-pointer"
                    >
                      {credLoading ? '⏳ Verifying OTP...' : '✓ Verify OTP & Continue'}
                    </button>

                    <button
                      type="button"
                      onClick={() => { setCredStep('form'); setCredOtp(''); setCredError(''); }}
                      className="text-xs text-slate-500 hover:text-slate-800 transition cursor-pointer"
                    >
                      ← Back to form
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ────────────────── STEP 2: ADD PARTICIPANTS (Image 1 & 2) ────────────────── */}
          {step === 2 && (
            <div>
              {/* Header */}
              <div className="hidden md:block text-[11px] font-bold tracking-wider text-[#19747C] uppercase mb-1">
                STEP 2 OF 3
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">Add participants</h2>
              <p className="text-sm text-slate-500 mt-1 mb-6">
                Add people to the meeting and assign their roles.
              </p>

              {/* ── Form Row ── */}
              {/* Desktop Form (flex row items-end) */}
              <div className="hidden md:flex items-end gap-3">
                {/* Mobile number */}
                <div className="flex-1 min-w-[280px]">
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Mobile number</label>
                  <div className="flex items-center border border-slate-300 rounded-lg bg-white px-3 py-2 focus-within:border-[#19747C] focus-within:ring-1 focus-within:ring-[#19747C] shadow-xs">
                    <span className="text-sm font-semibold text-slate-800 flex items-center gap-1 select-none">
                      +91
                      <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                    <div className="w-[1px] h-5 bg-slate-200 mx-2.5" />
                    <input
                      type="tel"
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      value={searchMobile}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setSearchMobile(val);
                        setSearchError('');
                      }}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchParticipant())}
                      className="w-full text-sm text-slate-800 placeholder:text-slate-400 bg-transparent border-none outline-none p-0"
                    />
                  </div>
                </div>

                {/* Participant role */}
                <div className="w-56">
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Participant role</label>
                  <div className="relative">
                    <select
                      value={searchRole}
                      onChange={e => setSearchRole(e.target.value)}
                      className="w-full appearance-none border border-slate-300 rounded-lg bg-white px-3 py-2 pr-8 text-sm font-medium text-slate-800 shadow-xs outline-none focus:border-[#19747C] cursor-pointer"
                    >
                      {ROLE_OPTIONS.filter(r => r.value !== 'ADMIN').map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Add button */}
                <div className="self-end pb-[1px]">
                  <button
                    type="button"
                    onClick={searchParticipant}
                    disabled={searchLoading}
                    className="bg-[#19747C] hover:bg-[#12656D] text-white font-semibold text-sm px-5 py-2 rounded-lg transition shadow-xs flex items-center justify-center gap-1.5 h-[38px] flex-shrink-0 cursor-pointer"
                  >
                    {searchLoading ? '...' : (
                      <>
                        <span className="text-base leading-none font-bold">+</span>
                        <span>Add</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Mobile Form (Image 2 Stacked layout) */}
              <div className="flex flex-col gap-3 md:hidden">
                {/* Mobile number */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Mobile number</label>
                  <div className="flex items-center border border-slate-300 rounded-lg bg-white px-3 py-2 focus-within:border-[#19747C] focus-within:ring-1 focus-within:ring-[#19747C] shadow-xs">
                    <span className="text-sm font-semibold text-slate-800 flex items-center gap-1 select-none">
                      +91
                      <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                    <div className="w-[1px] h-5 bg-slate-200 mx-2.5" />
                    <input
                      type="tel"
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      value={searchMobile}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setSearchMobile(val);
                        setSearchError('');
                      }}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchParticipant())}
                      className="w-full text-sm text-slate-800 placeholder:text-slate-400 bg-transparent border-none outline-none p-0"
                    />
                  </div>
                </div>

                {/* Participant role label + row with select & Add button */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Participant role</label>
                  <div className="flex items-center gap-2.5">
                    <div className="relative flex-1">
                      <select
                        value={searchRole}
                        onChange={e => setSearchRole(e.target.value)}
                        className="w-full appearance-none border border-slate-300 rounded-lg bg-white px-3 py-2 pr-8 text-sm font-medium text-slate-800 shadow-xs outline-none focus:border-[#19747C] cursor-pointer"
                      >
                        {ROLE_OPTIONS.filter(r => r.value !== 'ADMIN').map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={searchParticipant}
                      disabled={searchLoading}
                      className="bg-[#19747C] hover:bg-[#12656D] text-white font-semibold text-sm px-5 py-2 rounded-lg transition shadow-xs flex items-center justify-center gap-1.5 h-[38px] flex-shrink-0 cursor-pointer"
                    >
                      {searchLoading ? '...' : (
                        <>
                          <span className="text-base leading-none font-bold">+</span>
                          <span>Add</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Helper text */}
              <p className="text-[11px] text-slate-400 mt-1.5">
                Search using a 10-digit mobile number.
              </p>

              {searchError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-600 mt-2 font-medium">
                  ⚠️ {searchError}
                </div>
              )}

              {/* ── Participants Section ── */}
              <div className="flex items-center justify-between mt-8 mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-slate-900">Participants</h3>
                  <span className="bg-slate-100 text-slate-600 text-xs font-semibold px-2 py-0.5 rounded-md">
                    {participants.length}
                  </span>
                </div>
                <span className="text-xs text-slate-400">Including you</span>
              </div>

              {/* Desktop Table View (Image 1) */}
              <div className="hidden md:block border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                <div className="bg-[#f8fafc] border-b border-slate-200 px-5 py-2.5 flex items-center justify-between text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                  <span>PARTICIPANT</span>
                  <span className="w-64 text-left">ROLE</span>
                </div>

                <div className="divide-y divide-slate-100 bg-white">
                  {participants.map((p, idx) => {
                    const isRegistrar = p.partyRole === 'ADMIN';
                    return (
                      <div key={p.userId} className="px-5 py-3.5 flex items-center justify-between hover:bg-slate-50/50 transition">
                        {/* Left: Avatar + Name + Number */}
                        <div className="flex items-center gap-3.5">
                          {isRegistrar ? (
                            <div className="w-10 h-10 rounded-full bg-[#EAF4F4] text-[#19747C] font-bold text-sm flex items-center justify-center flex-shrink-0">
                              {(p.name || 'S')[0].toUpperCase()}
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-bold text-slate-900">
                                {isRegistrar ? (p.name || 'Registrar') : (p.name || `Participant ${idx + 1}`)}
                              </span>
                              {isRegistrar && <span className="text-xs text-slate-400 font-normal">(You)</span>}
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5 font-mono">
                              {isRegistrar ? p.mobileNumber : formatMaskedMobile(p.mobileNumber)}
                            </p>
                          </div>
                        </div>

                        {/* Right: Role display or dropdown + delete button */}
                        <div className="w-64 flex items-center justify-between">
                          {isRegistrar ? (
                            <div className="flex items-center gap-2.5">
                              <span className="text-sm font-semibold text-slate-800">Registrar</span>
                              <span className="bg-[#EAF4F4] text-[#19747C] text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                Admin
                              </span>
                            </div>
                          ) : (
                            <>
                              <div className="relative flex-1 mr-3">
                                <select
                                  value={p.partyRole}
                                  onChange={e => updateParticipantRole(p.userId, e.target.value)}
                                  className="w-full appearance-none border border-slate-200 rounded-lg bg-white px-3 py-1.5 pr-8 text-xs font-medium text-slate-700 outline-none focus:border-[#19747C] shadow-xs cursor-pointer"
                                >
                                  {ROLE_OPTIONS.filter(r => r.value !== 'ADMIN').map(r => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                  ))}
                                </select>
                                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeParticipant(p.userId)}
                                className="text-slate-400 hover:text-red-500 p-1.5 rounded-md transition cursor-pointer"
                                title="Remove participant"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Mobile Card-based View (Image 2) */}
              <div className="block md:hidden space-y-3">
                {participants.map((p, idx) => {
                  const isRegistrar = p.partyRole === 'ADMIN';
                  if (isRegistrar) {
                    return (
                      <div key={p.userId} className="border border-slate-200 rounded-xl p-3.5 bg-white shadow-xs">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#EAF4F4] text-[#19747C] font-bold text-sm flex items-center justify-center flex-shrink-0">
                              {(p.name || 'S')[0].toUpperCase()}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-bold text-slate-900">{p.name || 'Sanjana'}</span>
                                <span className="text-xs text-slate-400 font-normal">(You)</span>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">
                                Registrar • {p.mobileNumber}
                              </p>
                            </div>
                          </div>
                          <span className="bg-[#EAF4F4] text-[#19747C] text-xs font-semibold px-2.5 py-0.5 rounded-full">
                            Admin
                          </span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={p.userId} className="border border-slate-200 rounded-xl p-3.5 bg-white shadow-xs space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div>
                            <span className="text-sm font-bold text-slate-900 block">
                              {p.name || `Participant ${idx + 1}`}
                            </span>
                            <p className="text-xs text-slate-400 mt-0.5 font-mono">
                              {formatMaskedMobile(p.mobileNumber)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeParticipant(p.userId)}
                          className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"
                          title="Remove participant"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>

                      {/* Role selection block inside card */}
                      <div className="border border-slate-200 rounded-lg p-2.5 bg-slate-50/50">
                        <label className="block text-[10px] font-semibold uppercase text-slate-400 tracking-wider mb-1">Role</label>
                        <div className="relative">
                          <select
                            value={p.partyRole}
                            onChange={e => updateParticipantRole(p.userId, e.target.value)}
                            className="w-full appearance-none bg-transparent text-xs font-semibold text-slate-800 outline-none pr-6 cursor-pointer"
                          >
                            {ROLE_OPTIONS.filter(r => r.value !== 'ADMIN').map(r => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── Footer Navigation Buttons ── */}
              {/* Desktop Footer (Image 1) */}
              <div className="hidden md:flex items-center justify-between mt-10 pt-4">
                <button
                  type="button"
                  onClick={handleBackToStep1}
                  className="border border-[#19747C] text-[#19747C] hover:bg-[#EAF4F4] font-semibold px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 transition cursor-pointer"
                >
                  <span>←</span>
                  <span>Back</span>
                </button>

                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    disabled={participants.length < 2}
                    onClick={goToStep3}
                    className={`bg-[#19747C] hover:bg-[#12656D] text-white font-semibold px-6 py-2.5 rounded-lg text-sm flex items-center gap-2 transition shadow-xs cursor-pointer ${
                      participants.length < 2 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <span>Continue</span>
                    <span>→</span>
                  </button>
                  <span className="text-[11px] text-slate-400 mt-1.5">Next: Meeting details</span>
                </div>
              </div>

              {/* Mobile Footer (Image 2) */}
              <div className="flex md:hidden flex-col mt-8 pt-4">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleBackToStep1}
                    className="flex-1 border border-[#19747C] text-[#19747C] hover:bg-[#EAF4F4] font-semibold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <span>←</span>
                    <span>Back</span>
                  </button>
                  <button
                    type="button"
                    disabled={participants.length < 2}
                    onClick={goToStep3}
                    className={`flex-1 bg-[#19747C] hover:bg-[#12656D] text-white font-semibold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 shadow-xs cursor-pointer ${
                      participants.length < 2 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <span>Continue</span>
                    <span>→</span>
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-2 text-center">Next: Meeting details</p>
              </div>

            </div>
          )}

          {/* ────────────────── STEP 3: MEETING DETAILS ────────────────── */}
          {step === 3 && (
            <form onSubmit={createMeeting} className="space-y-6">
              <div>
                <div className="text-[11px] font-bold tracking-wider text-[#19747C] uppercase mb-1">
                  STEP 3 OF 3
                </div>
                <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">Meeting details</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Configure meeting title, reference codes, and redirect parameters.
                </p>
              </div>


              {/* Details Inputs */}
              <div className="border border-slate-200 rounded-xl p-5 bg-white shadow-xs space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Meeting Title *</label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Property Registration — Survey No. 123"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 outline-none focus:border-[#19747C] focus:ring-1 focus:ring-[#19747C] shadow-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Purpose *</label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Deed signing and witness verification"
                    value={purpose}
                    onChange={e => setPurpose(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 outline-none focus:border-[#19747C] focus:ring-1 focus:ring-[#19747C] shadow-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Document Registration ID</label>
                  <input
                    type="text"
                    placeholder="e.g. REG-2026-10-001"
                    value={docRegId}
                    onChange={e => setDocRegId(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 outline-none focus:border-[#19747C] focus:ring-1 focus:ring-[#19747C] shadow-xs"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Success Redirect URL</label>
                    <input
                      type="url"
                      placeholder="https://your-app.in/success"
                      value={navUrlSuccess}
                      onChange={e => setNavUrlSuccess(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 outline-none focus:border-[#19747C] focus:ring-1 focus:ring-[#19747C] shadow-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Failure Redirect URL</label>
                    <input
                      type="url"
                      placeholder="https://your-app.in/failure"
                      value={navUrlFailure}
                      onChange={e => setNavUrlFailure(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3.5 py-2 text-sm text-slate-800 outline-none focus:border-[#19747C] focus:ring-1 focus:ring-[#19747C] shadow-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Navigation Footer */}
              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="border border-[#19747C] text-[#19747C] hover:bg-[#EAF4F4] font-semibold px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 transition cursor-pointer"
                >
                  <span>←</span>
                  <span>Back</span>
                </button>

                <button
                  type="submit"
                  disabled={isCreating}
                  className="bg-[#19747C] hover:bg-[#12656D] text-white font-semibold px-6 py-2.5 rounded-lg text-sm flex items-center gap-2 transition shadow-xs cursor-pointer"
                >
                  {isCreating ? '⏳ Creating Meeting...' : '🚀 Create Meeting'}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}

export default function Page() {
  return (
    <>
      <main className={styles.main}>
        <img 
          src="/images/ab_logo.png" 
          alt="Logo" 
          className="h-10 sm:h-12 w-auto object-contain mx-auto" 
        />
        <Suspense fallback="Loading">
          <Tabs>
            <CreateMeetingTab label="Create Meeting" />
            <JoinMeetingTab label="Join Meeting" />
          </Tabs>
        </Suspense>
      </main>
      <footer>
        Powered by{' '}
        <a style={{color: '#19747C'}}>
          Atmik Bharat Industries Pvt Ltd
        </a>
      </footer>
    </>
  );
}
